import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { AlertTriangle, Info, RefreshCw } from 'lucide-react';
import { formatInfraDateTime, formatInfraDurationHours } from '../utils/infraAnalytics';
import { useInfraestruturaData } from '../context/InfraestruturaDataContext';
import osMetricsService from '../services/osMetricsService';
import OsSlaDetalheModal from './OsSlaDetalheModal';

const darkPanelClassName =
  'rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_14px_34px_rgba(15,23,42,0.08)]';

const chartTooltipStyle = {
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  color: 'var(--text-primary)',
  boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
};

const FAIXA_LABELS = {
  '0-1h': 'Até 1h',
  '1-2h': '1h a 2h',
  '2-4h': '2h a 4h',
  '4-8h': '4h a 8h',
  '8h+': 'Acima de 8h',
  sem_medicao: 'Sem medição',
};

const SLA_CARDS = [
  { key: 'dentro', label: 'Dentro do prazo', accent: 'text-emerald-500' },
  { key: 'atrasadas', label: 'Atrasadas', accent: 'text-rose-500' },
  { key: 'sem_prazo', label: 'Sem prazo definido', accent: 'text-[color:var(--text-muted)]' },
];

const isAbortError = (error) => (
  error?.name === 'AbortError'
  || error?.name === 'CanceledError'
  || error?.code === 'ERR_CANCELED'
);

const isIxcUnavailable = (error) => error?.response?.data?.code === 'IXC_UNAVAILABLE';

const formatDayLabel = (value) => formatInfraDateTime(`${value} 00:00:00`, { withTime: false });

const Notice = ({ tone = 'muted', icon: Icon, title, children, onRetry }) => (
  <div
    className={`flex items-start gap-3 rounded-[16px] border px-3 py-2.5 text-xs leading-5 ${
      tone === 'warning'
        ? 'border-amber-500/25 bg-amber-500/10 text-[color:var(--text-muted)]'
        : 'border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/55 text-[color:var(--text-muted)]'
    }`}
  >
    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone === 'warning' ? 'text-amber-500' : 'text-[color:var(--accent)]/85'}`} />
    <div className="min-w-0 flex-1">
      <p className="font-medium text-[color:var(--text-primary)]">{title}</p>
      <p>{children}</p>
    </div>
    {onRetry ? (
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] px-2.5 py-1 text-[11px] font-medium text-[color:var(--text-primary)] transition-colors hover:border-[color:var(--accent)]/45"
      >
        <RefreshCw className="h-3 w-3" />
        Tentar novamente
      </button>
    ) : null}
  </div>
);

const OsMetricsPanel = ({ isMobile = false }) => {
  const { range } = useInfraestruturaData();
  const [metrics, setMetrics] = useState(null);
  // Já nasce carregando quando há período: o efeito só roda após a pintura, e sem isso o
  // aviso de "sem métricas" pisca antes da primeira leitura. Sem período, nada será buscado.
  const [loading, setLoading] = useState(() => Boolean(range?.start && range?.end));
  const [errorMessage, setErrorMessage] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [slaDetalhe, setSlaDetalhe] = useState(null);
  const requestIdRef = useRef(0);

  const start = range?.start;
  const end = range?.end;

  // Trocar o período invalida o recorte aberto: os números seriam de outro intervalo.
  useEffect(() => {
    setSlaDetalhe(null);
  }, [start, end]);

  const retry = useCallback(() => setReloadToken((current) => current + 1), []);
  // Identidade estável: o modal usa onClose como dependência do efeito que trava a rolagem e
  // prende o foco. Uma função nova a cada render reiniciaria esse efeito e roubaria o foco de
  // quem estivesse digitando na busca.
  const fecharDetalhe = useCallback(() => setSlaDetalhe(null), []);

  useEffect(() => {
    if (!start || !end) {
      return undefined;
    }

    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const carregar = async () => {
      setLoading(true);
      setErrorMessage('');
      setUnavailable(false);

      try {
        const result = await osMetricsService.buscarMetricas(
          { data_inicio: start, data_fim: end },
          controller.signal,
        );

        if (requestId !== requestIdRef.current) {
          return;
        }

        setMetrics(result);
      } catch (error) {
        if (isAbortError(error) || requestId !== requestIdRef.current) {
          return;
        }

        setMetrics(null);
        if (isIxcUnavailable(error)) {
          setUnavailable(true);
          setErrorMessage(error?.response?.data?.message
            || 'A leitura direta de métricas de OS ainda não está habilitada para esta empresa.');
        } else {
          setErrorMessage(error?.response?.data?.message
            || error?.message
            || 'Não foi possível carregar as métricas de OS.');
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    carregar();

    return () => controller.abort();
  }, [end, reloadToken, start]);

  const progressao = useMemo(
    () => (metrics?.progressao || []).map((item) => ({
      ...item,
      label: formatDayLabel(item.data),
    })),
    [metrics],
  );

  const faixas = useMemo(
    () => (metrics?.faixa_duracao || []).map((item) => ({
      ...item,
      label: FAIXA_LABELS[item.faixa] || item.faixa,
    })),
    [metrics],
  );

  const cidades = useMemo(
    () => (metrics?.media_execucao_por_cidade || []).slice(0, 12),
    [metrics],
  );

  const execucaoInvalida = (metrics?.qualidade_execucao?.nao_positiva || 0)
    + (metrics?.qualidade_execucao?.sem_data || 0);

  // O padrão desta base é prazo pela meta do assunto e status_sla vazio. Só o que escapa disso
  // merece ser mostrado: prazo vindo do IXC, OS sem prazo apurável ou status_sla preenchido.
  const slaExcecoes = useMemo(() => {
    if (!metrics?.sla) {
      return [];
    }

    const excecoes = [];
    const { origem, por_status_ixc: porStatusIxc } = metrics.sla;

    if (origem?.ixc > 0) {
      excecoes.push({ chave: 'origem_ixc', rotulo: 'Prazo vindo de data_prazo_limite do IXC', total: origem.ixc });
    }

    if (origem?.sem_prazo > 0) {
      excecoes.push({ chave: 'origem_sem_prazo', rotulo: 'Sem prazo apurável', total: origem.sem_prazo });
    }

    (porStatusIxc || []).forEach((item) => {
      if (item.status !== 'nao_informado') {
        excecoes.push({
          chave: `status_${item.status}`,
          rotulo: `status_sla do IXC: ${item.status}`,
          total: item.total,
        });
      }
    });

    return excecoes;
  }, [metrics]);

  const chartTickProps = isMobile ? { fontSize: 10 } : { fontSize: 11 };
  const chartMargin = isMobile
    ? { top: 8, right: 8, left: -12, bottom: 8 }
    : { top: 8, right: 20, left: 0, bottom: 8 };
  const legendWrapperStyle = isMobile ? { paddingTop: 12, fontSize: 11 } : undefined;

  return (
    <div className="mt-3 grid grid-cols-1 gap-3" data-testid="os-metrics-panel">
      <div className={darkPanelClassName}>
        <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Métricas de execução e SLA</h3>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
              Agregados do período direto do IXC. Duração medida entre início e término da execução em campo.
            </p>
          </div>
          {loading ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] text-[color:var(--text-muted)]">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Carregando
            </span>
          ) : null}
        </div>

        {errorMessage ? (
          <Notice
            tone={unavailable ? 'muted' : 'warning'}
            icon={unavailable ? Info : AlertTriangle}
            title={unavailable ? 'Métricas indisponíveis' : 'Falha ao carregar métricas'}
            onRetry={retry}
          >
            {errorMessage}
          </Notice>
        ) : null}

        {!errorMessage && metrics ? (
          <div className="space-y-4">
            {execucaoInvalida > 0 ? (
              <Notice tone="warning" icon={AlertTriangle} title="Execuções sem duração confiável">
                {`${execucaoInvalida.toLocaleString('pt-BR')} OS do período não entram nas médias: `}
                {`${(metrics.qualidade_execucao.sem_data || 0).toLocaleString('pt-BR')} sem data de início ou término e `}
                {`${(metrics.qualidade_execucao.nao_positiva || 0).toLocaleString('pt-BR')} com término anterior ou igual ao início.`}
              </Notice>
            ) : null}

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
                  Progressão diária
                </h4>
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={progressao} margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.20)" />
                      <XAxis dataKey="label" tick={chartTickProps} stroke="var(--text-muted)" minTickGap={18} />
                      <YAxis tick={chartTickProps} width={isMobile ? 30 : 40} stroke="var(--text-muted)" allowDecimals={false} />
                      <Tooltip contentStyle={chartTooltipStyle} labelStyle={{ color: 'var(--text-primary)' }} itemStyle={{ color: 'var(--text-primary)' }} />
                      <Legend wrapperStyle={legendWrapperStyle} iconSize={isMobile ? 10 : 14} />
                      <Line type="monotone" dataKey="abertas" name="Abertas" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="fechadas" name="Finalizadas" stroke="#2563eb" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
                  Distribuição por faixa de duração
                </h4>
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={faixas} margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.20)" vertical={false} />
                      <XAxis dataKey="label" tick={chartTickProps} stroke="var(--text-muted)" />
                      <YAxis tick={chartTickProps} width={isMobile ? 30 : 40} stroke="var(--text-muted)" allowDecimals={false} />
                      <Tooltip contentStyle={chartTooltipStyle} labelStyle={{ color: 'var(--text-primary)' }} itemStyle={{ color: 'var(--text-primary)' }} />
                      <Bar dataKey="total" name="OS" fill="#2563eb" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
              <div className="xl:col-span-5">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
                  SLA no período
                </h4>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {SLA_CARDS.map((card) => (
                    <button
                      key={card.key}
                      type="button"
                      onClick={() => setSlaDetalhe(card.key)}
                      aria-haspopup="dialog"
                      className="rounded-[16px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-3 text-left transition-colors hover:border-[color:var(--accent)]/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
                    >
                      <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--text-muted)]">
                        {card.label}
                      </p>
                      <p className={`mt-1 text-2xl font-semibold leading-none ${card.accent}`}>
                        {(metrics.sla?.[card.key] || 0).toLocaleString('pt-BR')}
                      </p>
                      <p className="mt-1 text-[10px] text-[color:var(--text-muted)]">Ver ordens</p>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] leading-4 text-[color:var(--text-muted)]">
                  Prazo calculado pela meta do assunto, em horas corridas a partir da abertura — o
                  IXC quase não preenche data_prazo_limite nesta base.
                </p>

                {/* O detalhe só aparece quando algo foge do esperado; no caso normal ele seria
                    três linhas de constante ocupando espaço nobre. */}
                {slaExcecoes.length > 0 ? (
                  <div className="mt-3 rounded-[16px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.10em] text-[color:var(--text-muted)]">
                      Fora do padrão no período
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-[color:var(--text-primary)]">
                      {slaExcecoes.map((excecao) => (
                        <li key={excecao.chave} className="flex items-center justify-between gap-3">
                          <span>{excecao.rotulo}</span>
                          <span className="font-semibold">{excecao.total.toLocaleString('pt-BR')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              <div className="xl:col-span-7">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
                  Média de execução por cidade
                </h4>
                <div style={{ height: `${Math.max(cidades.length * 34, 240)}px` }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cidades} layout="vertical" margin={chartMargin}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.20)" horizontal={false} />
                      <XAxis type="number" tick={chartTickProps} stroke="var(--text-muted)" />
                      <YAxis
                        type="category"
                        dataKey="cidade"
                        tick={chartTickProps}
                        width={isMobile ? 90 : 140}
                        stroke="var(--text-muted)"
                      />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        labelStyle={{ color: 'var(--text-primary)' }}
                        itemStyle={{ color: 'var(--text-primary)' }}
                        formatter={(value) => formatInfraDurationHours(value)}
                      />
                      <Bar dataKey="media_horas" name="Média de execução" fill="#0ea5e9" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {!errorMessage && !metrics && !loading ? (
          <Notice icon={Info} title="Sem métricas no período" onRetry={retry}>
            Nenhum agregado foi retornado para o período selecionado.
          </Notice>
        ) : null}
      </div>

      <OsSlaDetalheModal
        situacao={slaDetalhe}
        range={range}
        isMobile={isMobile}
        onClose={fecharDetalhe}
      />
    </div>
  );
};

export default OsMetricsPanel;
